import React from 'react';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  elevated?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  onClick?: () => void;
  hover?: boolean;
}

const paddingMap = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

export function Card({
  children,
  className = '',
  elevated = false,
  padding = 'md',
  onClick,
  hover = false,
}: CardProps) {
  const baseClasses = 'rounded-xl border border-border transition-all duration-150';
  const bgClasses = elevated ? 'bg-surface-600 shadow-card' : 'bg-surface-700';
  const hoverClasses = hover || onClick ? 'hover:bg-surface-600 hover:shadow-card-hover cursor-pointer' : '';
  const paddingClasses = paddingMap[padding];

  return (
    <div
      className={`${baseClasses} ${bgClasses} ${hoverClasses} ${paddingClasses} ${className}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
}

export function CardHeader({ children, className = '', action }: CardHeaderProps) {
  return (
    <div className={`flex items-center justify-between mb-4 ${className}`}>
      <div className="text-h4 text-text-primary">{children}</div>
      {action && <div>{action}</div>}
    </div>
  );
}

interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export function CardContent({ children, className = '' }: CardContentProps) {
  return <div className={className}>{children}</div>;
}

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function CardFooter({ children, className = '' }: CardFooterProps) {
  return (
    <div className={`mt-4 pt-4 border-t border-border-subtle ${className}`}>
      {children}
    </div>
  );
}
